import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Archive, Image, Pencil, Plus } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDate, formatDateTime, formatNumber, hasPermission, labelOf, METER_SOURCE_LABELS } from '@blimo/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  ErrorState,
  PageHeader,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@blimo/ui';
import { DetailSkeleton, PropertyLink } from '../../components/data.jsx';
import { AddReadingDialog } from './add-reading-dialog.jsx';
import { MeterSheet } from './meter-sheet.jsx';
import { meterLabel } from './utils.js';

/** Zähler › Detail: Stammdaten und Ablese-Historie. */
export function MeterDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const { meterId } = useParams({ strict: false });
  const { data: meter, error, isPending, refetch } = useQuery(adminQueries.meter(api, meterId));
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const breadcrumb = (
    <Link to="/zaehler" className="hover:underline">
      Zähler & Versorger
    </Link>
  );
  if (error) {
    return (
      <>
        <PageHeader title="Zähler" breadcrumb={breadcrumb} />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Zähler nicht gefunden" />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;
  const canManage = hasPermission(me, 'meters.manage') && !meter.archived_at;
  const canAddReading = hasPermission(me, 'meter-readings.manage') && !meter.archived_at;
  const readings = [...(meter.readings ?? [])].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));

  async function archive() {
    try {
      await api.admin.meters.archive(meter.id);
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('meters') });
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-readings') });
      toast.success('Zähler archiviert');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Zähler konnte nicht archiviert werden.'));
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={`Zähler ${meterLabel(meter)}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <PropertyLink property={meter.property} />
            {meter.archived_at && <Badge variant="neutral">Archiviert seit {formatDate(meter.archived_at)}</Badge>}
          </span>
        }
        actions={
          canManage && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil aria-hidden="true" /> Bearbeiten
              </Button>
              <Button size="sm" variant="outline" onClick={() => setArchiveOpen(true)}>
                <Archive aria-hidden="true" /> Archivieren
              </Button>
            </div>
          )
        }
      />
      <div className="grid gap-3 lg:grid-cols-[22rem_1fr]">
        <Card>
          <CardHeader>
            <CardTitle as="h2">Stammdaten</CardTitle>
          </CardHeader>
          <CardContent>
            <DescriptionList
              labelWidth="8rem"
              items={[
                { label: 'Zählernummer', value: meter.number },
                { label: 'Zählerart', value: meter.type },
                { label: 'Vertragsnummer', value: meter.contract_number },
                { label: 'Versorger', value: meter.supplier?.name },
                { label: 'Ableser', value: meter.reader?.name },
                { label: 'Beschreibung', value: meter.description },
                { label: 'Quelle', value: labelOf(METER_SOURCE_LABELS, meter.source), hint: meter.cobra_id ? `Cobra-ID ${meter.cobra_id}` : undefined },
              ]}
            />
          </CardContent>
        </Card>
        <Card className="min-w-0">
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle as="h2">Ablese-Historie</CardTitle>
            {canAddReading && (
              <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
                <Plus aria-hidden="true" /> Zählerstand hinzufügen
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {readings.length === 0 ? (
              <EmptyState compact title="Keine Ablesungen" description="Für diesen Zähler wurden noch keine Ablesungen angelegt." />
            ) : (
              <Table aria-label="Ablese-Historie" containerClassName="rounded-md border">
                <TableHeader>
                  <TableRow>
                    <TableHead>Jahr</TableHead>
                    <TableHead data-numeric="true">Wert</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Abgelesen</TableHead>
                    <TableHead>Fotos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {readings.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="numeric">{r.year}</TableCell>
                      <TableCell data-numeric="true">{formatNumber(r.value, { maximumFractionDigits: 3 })}</TableCell>
                      <TableCell>
                        <span className="flex flex-wrap items-center gap-1">
                          <StatusBadge domain="meterReading" status={r.status} />
                          {r.is_new_meter && <span className="text-xs text-muted-foreground">neuer Zähler</span>}
                          {r.manual && <span className="text-xs text-muted-foreground">manuell</span>}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.read_at ? `${formatDateTime(r.read_at)}${r.read_by?.name ? ` · ${r.read_by.name}` : ''}` : '–'}
                      </TableCell>
                      <TableCell>
                        <span className="flex gap-2">
                          {[r.has_photo && 1, r.has_photo_2 && 2].filter(Boolean).map((slot) => (
                            <a
                              key={slot}
                              href={api.admin.meterReadings.photoUrl(r.id, slot)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                              aria-label={`Foto ${slot} der Ablesung ${r.year}`}
                            >
                              <Image className="size-3.5" aria-hidden="true" /> {slot}
                            </a>
                          ))}
                          {!r.has_photo && !r.has_photo_2 && <span className="text-muted-foreground">–</span>}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      <MeterSheet open={editOpen} onOpenChange={setEditOpen} meter={meter} />
      {canAddReading && <AddReadingDialog meter={meter} open={addOpen} onOpenChange={setAddOpen} />}
      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="Zähler archivieren?"
        description="Offene Ablesungen werden entfernt, die Ablese-Historie bleibt erhalten."
        confirmLabel="Archivieren"
        destructive
        onConfirm={archive}
      />
    </>
  );
}
